/**
 * RoDialog - 自定义模态对话框 Web 组件
 *
 * 功能特性：
 * - 基于 Web Components 标准开发，支持 Shadow DOM 隔离样式
 * - 响应式设计：桌面端为居中模态框，移动端为底部弹窗
 * - 支持 open/close 方法和属性控制显示隐藏
 * - 提供 beforeclose 事件用于拦截关闭操作
 * - 提供 onopen/onclose 事件用于状态监听
 * - 移动端支持拖拽关闭手势
 * - 支持 ESC 键关闭（可拦截）
 * - 自动动画过渡效果
 *
 * 使用方法：
 * 1. 引入组件后，在 HTML 中使用：<ro-dialog><p>对话框内容</p></ro-dialog>
 * 2. 通过 JavaScript 控制：
 *    const dialog = document.querySelector('ro-dialog');
 *    dialog.open();  // 打开对话框
 *    dialog.close(); // 关闭对话框
 *
 * 3. 通过属性控制：
 *    <ro-dialog open>  <!-- 初始打开状态 -->
 *    <ro-dialog width="600px">  <!-- 设置对话框宽度 -->
 *
 * 事件监听：
 * - beforeclose: 关闭前触发，可调用 e.preventDefault() 阻止关闭
 * - onclose: 关闭后触发
 * - onopen: 打开后触发
 *
 * 示例：
 * ```
 * <ro-dialog id="myDialog">
 *   <h2>标题</h2>
 *   <p>对话框内容</p>
 * </ro-dialog>
 *
 * <script>
 *   const dialog = document.getElementById('myDialog');
 *   dialog.addEventListener('beforeclose', (e) => {
 *     if (!confirm('确定要关闭吗？')) e.preventDefault();
 *   });
 *   dialog.open();
 * </script>
 * ```
 */
class RoDialog extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._isOpen = false;
    this._transformReleaseTimer = null;

    this.shadowRoot.innerHTML = `
      <style>
        dialog {
          background: var(--ro-dialog-bg, #fff);
          border-radius: 4px;
          border: none;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          max-height: 90vh;
          padding: 0;
          width: var(--ro-dialog-width, 500px);
        }

        dialog::backdrop {
          background: rgba(0, 0, 0, 0.5);
        }

        .header {
          display: flex;
          justify-content: end;
          padding: 16px;
          color: #161616;
        }

        :host([mode="simple"]) .header {
          display: none;
        }

        .touchbar {
          display: none;
          text-align: center;
        }

        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
        }

        .close-btn:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .close-btn:active {
          background: rgba(0, 0, 0, 0.12);
        }

        .content {
          padding: 0 56px 56px 56px;
          overflow-y: auto;
        }

        /* Simple mode styles */
        :host([mode="simple"]) .content {
          padding: 24px;
        }

        :host([mode="simple"]) dialog {
          border-radius: 4px;
        }

        /* Mobile styles */
        @media (max-width: 1183px) {
          dialog {
            width: 100%;
            border-radius: 16px 16px 0 0;
            max-width: none;
            max-height: calc(100% - 113px);
            margin: auto 0 0;
          }

          dialog[open] {
            display: flex;
            flex-direction: column;
          }

          .touchbar {
            display: flex;
            justify-content: center;
          }

          .header {
            flex-direction: column;
            padding: 8px 24px;
          }

          .close-btn {
            display: none;
          }

          .content {
            padding: 0 24px 24px;
          }

          /* Simple mode mobile: center instead of bottom */
          :host([mode="simple"]) dialog {
            width: auto;
            max-width: min(400px, calc(100% - 32px));
            border-radius: 4px;
            margin: auto;
            max-height: 90vh;
          }

          :host([mode="simple"]) .content {
            padding: 24px;
          }
        }

        /* Animation */
        dialog {
          transform: translateY(-20px);
          opacity: 0;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        dialog[open] {
          transform: translateY(0);
          opacity: 1;
        }

        @media (max-width: 1183px) {
          dialog {
            transform: translateY(100%);
          }

          dialog[open] {
            transform: translateY(0);
          }

          /* Simple mode: fade animation instead of slide up */
          :host([mode="simple"]) dialog {
            transform: translateY(-20px);
          }

          :host([mode="simple"]) dialog[open] {
            transform: translateY(0);
          }
        }
      </style>

      <dialog>
        <div class="header">
          <div class="touchbar">
            <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 11L12 14L21 11" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="round"/>
            </svg>
          </div>
          <button class="close-btn" aria-label="Close">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16.9797 7.05591L7.0673 16.9425" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
              <path d="M7.04993 7.05078L16.9494 16.9503" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="content">
          <slot></slot>
        </div>
      </dialog>
    `;

    this.dialog = this.shadowRoot.querySelector("dialog");
    this.closeBtn = this.shadowRoot.querySelector(".close-btn");
    this.touchbar = this.shadowRoot.querySelector(".touchbar");
  }

  connectedCallback() {
    this.closeBtn.addEventListener("click", () => this.close());
    // 点击遮罩关闭弹窗，同时阻止事件冒泡，避免触发外部点击监听器。
    this.dialog.addEventListener("click", (e) => {
      e.stopPropagation();

      if (e.target === this.dialog) {
        this.close();
      }
    });

    // 监听原生dialog的关闭事件
    this.dialog.addEventListener("close", () => {
      if (this._isOpen) {
        this._isOpen = false;
        this.removeAttribute("open");
        this.dispatchEvent(
          new CustomEvent("onclose", {
            bubbles: true,
            detail: { dialog: this },
          }),
        );
      }
    });

    // 监听ESC键关闭，触发beforeclose事件以允许拦截
    this.dialog.addEventListener("cancel", (e) => {
      // 先阻止默认行为（关闭dialog）
      e.preventDefault();

      // 触发beforeclose事件
      const beforeCloseEvent = new CustomEvent("beforeclose", {
        bubbles: true,
        cancelable: true,
        detail: { dialog: this },
      });

      this.dispatchEvent(beforeCloseEvent);

      if (beforeCloseEvent.defaultPrevented) {
        // 如果被阻止，保持dialog打开状态
        // 不需要做任何操作，因为已经阻止了默认行为
      } else {
        // 如果未被阻止，直接关闭dialog并更新状态（不调用this.close()避免循环）
        this._isOpen = false;
        this.dialog.close();
        this.removeAttribute("open");
        this._resetDialogStyles();

        this.dispatchEvent(
          new CustomEvent("onclose", {
            bubbles: true,
            detail: { dialog: this },
          }),
        );
      }
    });

    // 添加touchbar交互
    this._setupTouchbarInteractions();
  }

  _setupTouchbarInteractions() {
    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    const dragThreshold = 100;

    // 点击touchbar关闭
    this.touchbar.addEventListener("click", () => {
      this.close();
    });

    // Touch events for drag to close
    this.touchbar.addEventListener("touchstart", (e) => {
      startY = e.touches[0].clientY;
      isDragging = true;
    });

    this.touchbar.addEventListener("touchmove", (e) => {
      if (!isDragging) return;

      currentY = e.touches[0].clientY;
      const diff = currentY - startY;

      if (diff > 0) {
        this.dialog.style.transform = `translateY(${diff}px)`;
        this.dialog.style.transition = "none";
        e.preventDefault();
      }
    });

    this.touchbar.addEventListener("touchend", (e) => {
      if (!isDragging) return;

      const diff = currentY - startY;

      if (diff > dragThreshold) {
        this.close();
      } else {
        this.dialog.style.transition = "";
        this.dialog.style.transform = "none";
      }

      isDragging = false;
    });

    this.touchbar.addEventListener("touchcancel", () => {
      isDragging = false;
      this._resetDialogStyles();
    });
  }

  _resetDialogStyles() {
    if (this._transformReleaseTimer) {
      clearTimeout(this._transformReleaseTimer);
      this._transformReleaseTimer = null;
    }

    // 重置所有可能在拖拽过程中被修改的样式
    this.dialog.style.transform = "";
    this.dialog.style.transition = "";
    this.dialog.style.removeProperty("transform");
    this.dialog.style.removeProperty("transition");
  }

  _releaseTransformAfterOpen() {
    if (this._transformReleaseTimer) {
      clearTimeout(this._transformReleaseTimer);
    }

    this._transformReleaseTimer = setTimeout(() => {
      if (this._isOpen) {
        this.dialog.style.transform = "none";
      }
      this._transformReleaseTimer = null;
    }, 300);
  }

  _clearDefaultFocus() {
    const activeElement = this.shadowRoot.activeElement;

    if (activeElement === this.closeBtn || activeElement === this.dialog) {
      activeElement.blur();
    }
  }

  open() {
    if (this._isOpen) return;

    this._isOpen = true;
    this.setAttribute("open", "");
    this.dialog.showModal();
    this._clearDefaultFocus();
    requestAnimationFrame(() => this._clearDefaultFocus());
    this._releaseTransformAfterOpen();

    this.dispatchEvent(
      new CustomEvent("onopen", {
        bubbles: true,
        detail: { dialog: this },
      }),
    );
  }

  close() {
    if (!this._isOpen) return;

    // 触发 beforeclose 事件
    const beforeCloseEvent = new CustomEvent("beforeclose", {
      bubbles: true,
      cancelable: true,
      detail: { dialog: this },
    });

    this.dispatchEvent(beforeCloseEvent);

    if (beforeCloseEvent.defaultPrevented) {
      return;
    }

    this._isOpen = false;
    this.dialog.close();
    this.removeAttribute("open");

    // 重置所有可能被修改的样式
    this._resetDialogStyles();

    this.dispatchEvent(
      new CustomEvent("onclose", {
        bubbles: true,
        detail: { dialog: this },
      }),
    );
  }

  _updateMode(mode) {
    // Mode attribute changes are handled via CSS :host([mode="simple"])
    // This method is here for potential future JavaScript-based mode logic
  }

  get isOpen() {
    return this._isOpen;
  }

  static get observedAttributes() {
    return ["open", "width", "mode"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open") {
      this._isOpen = newValue !== null;
      if (this._isOpen) {
        this.open();
      } else {
        this.close();
      }
    } else if (name === "width") {
      this.style.setProperty("--ro-dialog-width", newValue || "500px");
    } else if (name === "mode") {
      this._updateMode(newValue);
    }
  }
}

// Register the custom element
if (!customElements.get("ro-dialog")) {
  customElements.define("ro-dialog", RoDialog);
}
